import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tray, PencilSimpleLine, At, Wrench, Circle, ArrowCounterClockwise } from "@phosphor-icons/react";
import { api, fmtDateTime, STATUS_LABELS, STATUS_COLORS } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { InboxThread, ticketHref } from "./InboxThread";
import { InboxCompose } from "./InboxCompose";
import { ChatList, ChatView, ChatEmpty } from "./ChatPanel";

const TABS = [["todo", "To do"], ["chats", "Chats"], ["done", "Done"]];
const byNewest = (a, b) => (b.at || "").localeCompare(a.at || "");

function MentionRow({ n, canAct, onChange }) {
  const nav = useNavigate();
  const toggle = async (e) => { e.stopPropagation(); await api.post(`/notifications/${n.id}/done`, { done: !n.done }); onChange(); };
  return (
    <div onClick={() => nav(ticketHref(n.ticket_id, { note: n.note_id }))} data-testid={`inbox-mention-${n.id}`}
      className={`cursor-pointer rounded-xl border px-4 py-3 flex items-start gap-3 transition-colors hover:border-[#BFDBFE] ${n.done ? "border-[#E5E7EB] bg-white opacity-70" : "border-[#E5E7EB] bg-white"}`}>
      <span className="w-[34px] h-[34px] rounded-full bg-[#DBEAFE] flex items-center justify-center shrink-0"><At size={16} weight="bold" className="text-[#1E5FEB]" /></span>
      <div className="flex-1 min-w-0">
        <div className="text-[12px] text-slate-500"><b className="text-slate-800">{n.by}</b> mentioned you{n.ticket_number ? <> on <span className="font-mono text-[#1E5FEB]">{n.ticket_number}</span></> : null} · {fmtDateTime(n.created_at)}</div>
        <div className="mt-0.5 text-[13.5px] text-slate-900 line-clamp-1">{n.text}</div>
      </div>
      {canAct && (
        <button onClick={toggle} data-testid={`inbox-mention-done-${n.id}`} title={n.done ? "Move back to To do" : "Mark as done"}
          className={`h-8 px-3 rounded-lg border text-[11.5px] font-semibold inline-flex items-center gap-1.5 shrink-0 transition-colors ${n.done ? "border-[#E5E7EB] bg-white text-slate-500 hover:bg-slate-50" : "border-[#E5E7EB] bg-white text-slate-700 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700"}`}>
          {n.done ? <><ArrowCounterClockwise size={13} weight="bold" />Undo</> : <><Circle size={13} weight="bold" />Done</>}
        </button>
      )}
    </div>
  );
}

function TicketRow({ t }) {
  const nav = useNavigate();
  return (
    <div onClick={() => nav(`/repairs/${t.id}`)} data-testid={`inbox-my-ticket-${t.ticket_number}`}
      className="cursor-pointer rounded-xl border border-[#E5E7EB] bg-white px-4 py-3 flex items-center gap-3 hover:border-[#BFDBFE] transition-colors">
      <span className="w-[34px] h-[34px] rounded-full bg-slate-100 flex items-center justify-center shrink-0"><Wrench size={16} weight="bold" className="text-slate-600" /></span>
      <div className="flex-1 min-w-0">
        <div className="text-[13.5px] font-semibold text-slate-900 truncate"><span className="font-mono text-[#1E5FEB]">{t.ticket_number}</span> · {[t.device_make, t.device_model].filter(Boolean).join(" ")}</div>
        <div className="text-[12px] text-slate-500 truncate">Ticket assigned to you · {t.customer_name}</div>
      </div>
      <span className="h-6 px-2 rounded-md border text-[11px] font-bold inline-flex items-center gap-1.5 shrink-0" style={{ color: STATUS_COLORS[t.status], borderColor: `color-mix(in srgb, ${STATUS_COLORS[t.status]} 30%, white)`, background: `color-mix(in srgb, ${STATUS_COLORS[t.status]} 8%, white)` }}>
        {STATUS_LABELS[t.status]}
      </span>
    </div>
  );
}

export function StaffInbox() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [viewing, setViewing] = useState("");
  const [staff, setStaff] = useState([]);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("todo");
  const [compose, setCompose] = useState(false);
  const [chatWith, setChatWith] = useState(null);
  const me = viewing || user?.id;

  const load = useCallback(() => {
    api.get("/inbox", { params: viewing ? { user_id: viewing } : {} }).then((r) => setData(r.data)).catch(() => {});
  }, [viewing]);
  useEffect(() => { load(); const t = setInterval(load, tab === "chats" ? 4000 : 30000); return () => clearInterval(t); }, [load, tab]);
  useEffect(() => { api.get("/inbox/staff").then((r) => setStaff(r.data)).catch(() => {}); }, []);
  const staffMap = useMemo(() => Object.fromEntries(staff.map((s) => [s.id, s])), [staff]);

  const lists = useMemo(() => {
    if (!data) return null;
    const uid = data.user_id;
    const tasks = [...data.received, ...data.sent].filter((m) => m.kind === "task");
    const openTask = (m) => m.task_status !== "done";
    return {
      todo: [...tasks.filter(openTask).map((m) => ({ t: "msg", m, at: m.updated_at })),
             ...data.mentions.filter((n) => !n.done).map((n) => ({ t: "mention", n, at: n.created_at })),
             ...data.my_tickets.map((tk) => ({ t: "ticket", tk, at: tk.assigned_at }))].sort(byNewest),
      done: [...tasks.filter((m) => !openTask(m)).map((m) => ({ t: "msg", m, at: m.updated_at })),
             ...data.mentions.filter((n) => n.done).map((n) => ({ t: "mention", n, at: n.created_at }))].sort(byNewest).slice(0, 30),
      chats: { ...data, received: data.received.filter((m) => m.kind === "message"), sent: data.sent.filter((m) => m.kind === "message") },
      unread: [...data.received, ...data.sent].filter((m) => m.kind === "message" && (m.unread_for || []).includes(uid)).length,
    };
  }, [data]);

  const own = !viewing || viewing === user?.id;
  const count = (k) => (k === "todo" ? lists?.todo.length : k === "chats" ? lists?.unread : null);
  const empty = { todo: "Nothing to do. You're all caught up.", done: "Finished tasks and mentions will show here." };
  const Row = (r) => r.t === "msg"
    ? <InboxThread key={r.m.id} m={r.m} me={me} actor={user?.id} onChange={load} showSentStatus={r.m.from_id === me} fadeOnDone={tab === "todo"} />
    : r.t === "mention" ? <MentionRow key={r.n.id} n={r.n} canAct={own} onChange={load} /> : <TicketRow key={r.tk.id} t={r.tk} />;

  return (
    <div className="space-y-3">
    <section className="rounded-2xl border border-[#E5E7EB] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.05)] overflow-hidden" data-testid="staff-inbox">
      <div className="px-5 py-4 flex flex-wrap items-center justify-between gap-3 border-b border-[#F1F5F9]">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-xl bg-[#EFF6FF] flex items-center justify-center"><Tray size={18} weight="bold" className="text-[#1E5FEB]" /></span>
          <div>
            <div className="text-[15px] font-bold text-slate-900">{own ? "My inbox" : `${staff.find((s) => s.id === viewing)?.name || "Staff"}'s inbox`}</div>
            <div className="text-[11.5px] text-slate-500">{own ? "What needs you, and your team chats" : "What needs them, and their chats"}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <select value={viewing} onChange={(e) => setViewing(e.target.value)} data-testid="inbox-viewing-select"
              className="h-9 px-3 rounded-xl border border-[#E5E7EB] bg-white text-[12.5px] font-semibold text-slate-700 focus:outline-none focus:border-[#1E5FEB]">
              <option value="">My inbox</option>
              {staff.map((s) => <option key={s.id} value={s.id}>View {s.name}</option>)}
            </select>
          )}
          <button onClick={() => setCompose(true)} data-testid="inbox-compose-btn"
            className="h-9 px-4 rounded-xl bg-[#1E5FEB] hover:bg-[#1849C8] text-white text-[12.5px] font-bold inline-flex items-center gap-1.5 whitespace-nowrap transition-colors">
            <PencilSimpleLine size={14} weight="bold" /> <span className="hidden sm:inline">{isAdmin ? "New message / task" : "New message"}</span><span className="sm:hidden">New</span>
          </button>
        </div>
      </div>
      <div className="px-5 pt-3 flex gap-1 overflow-x-auto" data-testid="inbox-tabs">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => { setTab(k); setChatWith(null); }} data-testid={`inbox-tab-${k}`}
            className={`h-8 px-3 rounded-lg text-[12.5px] font-semibold whitespace-nowrap transition-colors ${tab === k ? "bg-[#EFF6FF] text-[#1E5FEB]" : "text-slate-500 hover:text-slate-800 hover:bg-slate-50"}`}>
            {l}{count(k) ? <span className={`ml-1.5 px-1.5 rounded-full text-[10.5px] ${tab === k ? "bg-[#1E5FEB] text-white" : "bg-slate-200 text-slate-600"}`}>{count(k)}</span> : null}
          </button>
        ))}
      </div>
      {lists && tab === "chats" && own ? (
        <div className="mt-3 border-t border-[#EEF2F6] grid md:grid-cols-[300px_1fr] h-[calc(100dvh-220px)] min-h-[420px] md:h-[540px]" data-testid="inbox-list-chats">
          <div className={`${chatWith ? "hidden md:block" : "block"} min-h-0 overflow-y-auto p-2 border-r border-[#EEF2F6] space-y-0.5`} data-testid="chat-people">
            <ChatList data={lists.chats} me={me} staff={staffMap} activeId={chatWith?.id} onOpen={setChatWith} />
          </div>
          <div className={`${chatWith ? "block" : "hidden md:block"} min-h-0`}>
            {chatWith
              ? <ChatView key={chatWith.id} person={chatWith} me={me} store={staffMap[chatWith.id]?.store_name} onBack={() => setChatWith(null)} onChanged={load} />
              : <ChatEmpty />}
          </div>
        </div>
      ) : (
      <div className="p-5 pt-3 space-y-2 max-h-[460px] overflow-y-auto" data-testid={`inbox-list-${tab}`}>
        {!lists && <div className="text-[13px] text-slate-400 py-6 text-center">Loading…</div>}
        {lists && tab !== "chats" && lists[tab].length === 0 && <div className="text-[13px] text-slate-500 py-8 text-center" data-testid="inbox-empty">{empty[tab]}</div>}
        {lists && tab !== "chats" && lists[tab].map(Row)}
        {lists && tab === "chats" && [...lists.chats.received, ...lists.chats.sent].sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""))
          .map((m) => <InboxThread key={m.id} m={m} me={me} actor={user?.id} onChange={load} showSentStatus={m.from_id === me} />)}
      </div>
      )}
      <InboxCompose open={compose} onClose={() => setCompose(false)} onSent={load} isAdmin={isAdmin} />
    </section>
    </div>
  );
}
